import { LocalStorageIo } from '@aninix/api'
import { useGetProjectMeta } from '@aninix/api/get-project-meta'
import featureFlags from '@aninix/core/feature-flags'
import { useGetComments } from '@aninix/core/use-cases/comments/use-get-comments'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useParams } from 'react-router-dom'
import { InspectMode } from '../../components/toggle-menu'
import { useInspectMode } from '../use-inspect-mode'

const localProjectCommentsCount = new LocalStorageIo<Record<string, number>>(
  'aninix.project-comments-counters',
  {}
)

export const Tools: React.FCC = observer(() => {
  const { projectId } = useParams()
  const { data, refetch } = useGetProjectMeta(projectId ?? '')

  const { query: getCommentsQuery } = useGetComments({
    isStartingLoading: false,
  })

  React.useEffect(() => {
    // this will ensure metadata of the project is reloaded
    refetch()
  }, [getCommentsQuery?.data, projectId])

  const [displayCommentsBadge, setDisplayCommentsBadge] =
    React.useState<boolean>(false)

  const { inspectMode } = useInspectMode()

  React.useEffect(() => {
    const projectCommentsCount = data?.data.totalCommentsCount ?? 0

    if (projectId === undefined) return

    // this will remove badge on changing tool to comments and update localStorage
    if (inspectMode === 'comments' && projectCommentsCount > 0) {
      localProjectCommentsCount.update({ [projectId]: projectCommentsCount })
      setDisplayCommentsBadge(false)
    }
  }, [inspectMode, data?.data.totalCommentsCount, projectId])

  React.useEffect(() => {
    const projectCommentsCount = data?.data.totalCommentsCount ?? 0

    if (projectId === undefined) return

    const value = localProjectCommentsCount.get()
    // this will check if badge is required on initial state (when localStorage is empty)
    if (value === null || value[projectId] === undefined) {
      localProjectCommentsCount.update({ [projectId]: 0 })
      if (projectCommentsCount > 0) setDisplayCommentsBadge(true)
      return
    }

    // this will check if badge is required if all the data is here
    if (projectCommentsCount > value[projectId]) {
      setDisplayCommentsBadge(true)
      return
    }

    // since early returns, if no checks has been triggered, then badge is not required
    setDisplayCommentsBadge(false)
  }, [data?.data.totalCommentsCount, localProjectCommentsCount, projectId])

  return (
    <div className="flex flex-row gap-2 items-center pl-2">
      <InspectModeButton mode={'preview'} Icon={PreviewIcon} />
      <InspectModeButton mode={'details'} Icon={DetailsIcon} />
      <InspectModeButton
        mode={'comments'}
        Icon={CommentsIcon}
        displayBadge={displayCommentsBadge}
      />

      {featureFlags.overrides && (
        <InspectModeButton mode={'overrides'} Icon={OverridesIcon} />
      )}
    </div>
  )
})

interface IModeButton {
  mode: InspectMode
  Icon: (props: React.HTMLAttributes<SVGSVGElement>) => React.JSX.Element
  displayBadge?: boolean
}

const modeOrder: InspectMode[] = ['preview', 'details', 'comments', 'overrides']

const InspectModeButton: React.FCC<IModeButton> = ({
  mode,
  Icon,
  displayBadge,
}) => {
  const { inspectMode, setInspectMode } = useInspectMode()

  const isActive = inspectMode === mode

  const offset = modeOrder.indexOf(inspectMode) - modeOrder.indexOf(mode)

  const toRight = offset > 0

  const toLeft = offset < 0

  const isFar = Math.abs(offset) >= 2

  const handleSelection = React.useCallback(() => {
    setInspectMode(mode)
  }, [])

  return (
    <button
      onClick={handleSelection}
      className={classNames(
        'group relative flex h-8 w-8 items-center justify-center rounded-lg hover:cursor-default'
      )}
    >
      {/* Badge */}
      <div
        className={classNames(
          'absolute right-0.5 top-0.5 z-20 h-2 w-2 origin-center rounded-full bg-red transition-opacity duration-150',
          { ['scale-0 opacity-0']: !displayBadge }
        )}
      />

      <div
        className={classNames(
          'absolute inset-0 z-0 rounded-lg bg-white opacity-0 transition-opacity duration-150',
          { ['group-hover:!opacity-10']: !isActive }
        )}
      />
      <div
        className={classNames(
          'pointer-events-none absolute inset-0 z-0 rounded-lg bg-accent transition-[transform,opacity] duration-150 ease-in-out',
          {
            ['opacity-0']: !isActive,
            ['translate-x-10']: toRight && !isFar,
            ['-translate-x-10']: toLeft && !isFar,
            ['translate-x-20']: toRight && isFar,
            ['-translate-x-20']: toLeft && isFar,
            ['pointer-events-none']: toLeft || toRight || isFar,
          }
        )}
      />
      <Icon
        className={classNames('z-10 h-6 w-6', { ['opacity-50']: !isActive })}
      />
    </button>
  )
}

const PreviewIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 12C10 12.5304 10.2107 13.0391 10.5858 13.4142C10.9609 13.7893 11.4696 14 12 14C12.5304 14 13.0391 13.7893 13.4142 13.4142C13.7893 13.0391 14 12.5304 14 12C14 11.4696 13.7893 10.9609 13.4142 10.5858C13.0391 10.2107 12.5304 10 12 10C11.4696 10 10.9609 10.2107 10.5858 10.5858C10.2107 10.9609 10 11.4696 10 12Z"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 12C18.5997 16.0003 15.6 18 12 18C8.4 18 5.4003 16.0003 3 12C5.4003 7.99971 8.4 6 12 6C15.6 6 18.5997 7.99971 21 12Z"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const DetailsIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17 8L21 12L17 16M7 8L3 12L7 16M14 4L10 20"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CommentsIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 20.0001L4.3 16.1001C3.17644 14.4384 2.76999 12.4705 3.15622 10.5624C3.54244 8.65427 4.69506 6.93575 6.39977 5.72635C8.10447 4.51696 10.2453 3.89898 12.4241 3.98732C14.6029 4.07566 16.6715 4.86431 18.2453 6.20664C19.819 7.54896 20.7909 9.35362 20.9801 11.2851C21.1693 13.2165 20.563 15.1433 19.2739 16.7072C17.9848 18.2711 16.1007 19.3657 13.9718 19.7874C11.8429 20.2092 9.6142 19.9294 7.7 19.0001L3 20.0001Z"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const OverridesIcon = (props: React.HTMLAttributes<SVGSVGElement>) => (
  <svg
    {...props}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 9L15 12L10 15V9Z"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.43597 17.816L5.14878 17.4528H5.14878L4.43597 17.816ZM10.4 20.8C10.8418 20.8 11.2 20.4418 11.2 20C11.2 19.5582 10.8418 19.2 10.4 19.2V20.8ZM6.18404 19.564L6.54723 18.8512H6.54723L6.18404 19.564ZM6.18404 4.43597L5.82085 3.72317V3.72317L6.18404 4.43597ZM4.43597 6.18404L5.14878 6.54723L5.14878 6.54723L4.43597 6.18404ZM17.816 4.43597L17.4528 5.14878V5.14878L17.816 4.43597ZM19.2 10.4C19.2 10.8418 19.5582 11.2 20 11.2C20.4418 11.2 20.8 10.8418 20.8 10.4H19.2ZM19.564 6.18404L20.2768 5.82085V5.82085L19.564 6.18404ZM20 13.5L20.5657 12.9343C20.2533 12.6219 19.7467 12.6219 19.4343 12.9343L20 13.5ZM13.6 20L13.02 19.449C12.7267 19.7578 12.7267 20.2422 13.02 20.551L13.6 20ZM17.4343 14.9343C17.1219 15.2467 17.1219 15.7533 17.4343 16.0657C17.7467 16.3781 18.2533 16.3781 18.5657 16.0657L17.4343 14.9343ZM21.4343 16.0657C21.7467 16.3781 22.2533 16.3781 22.5657 16.0657C22.8781 15.7533 22.8781 15.2467 22.5657 14.9343L21.4343 16.0657ZM16.08 18.551C16.3843 18.2307 16.3713 17.7243 16.051 17.42C15.7307 17.1157 15.2243 17.1287 14.92 17.449L16.08 18.551ZM14.92 22.551C15.2243 22.8713 15.7307 22.8843 16.051 22.58C16.3713 22.2757 16.3843 21.7693 16.08 21.449L14.92 22.551ZM10.4 4.8H13.6V3.2H10.4V4.8ZM3.2 13.6C3.2 14.7069 3.19938 15.5852 3.25715 16.2923C3.31569 17.0088 3.43814 17.6198 3.72317 18.1792L5.14878 17.4528C4.99783 17.1565 4.90229 16.7796 4.85184 16.162C4.80062 15.5352 4.8 14.7333 4.8 13.6H3.2ZM10.4 19.2C9.26669 19.2 8.46483 19.1994 7.83795 19.1482C7.22042 19.0977 6.84348 19.0022 6.54723 18.8512L5.82085 20.2768C6.38024 20.5619 6.99118 20.6843 7.70766 20.7428C8.4148 20.8006 9.2931 20.8 10.4 20.8V19.2ZM3.72317 18.1792C4.18336 19.0823 4.91767 19.8166 5.82085 20.2768L6.54723 18.8512C5.94511 18.5444 5.45557 18.0549 5.14878 17.4528L3.72317 18.1792ZM10.4 3.2C9.2931 3.2 8.4148 3.19938 7.70766 3.25715C6.99118 3.31569 6.38024 3.43814 5.82085 3.72317L6.54723 5.14878C6.84348 4.99783 7.22042 4.90229 7.83795 4.85184C8.46483 4.80062 9.26669 4.8 10.4 4.8V3.2ZM4.8 10.4C4.8 9.26669 4.80062 8.46483 4.85184 7.83795C4.90229 7.22042 4.99783 6.84348 5.14878 6.54723L3.72317 5.82085C3.43814 6.38024 3.31569 6.99118 3.25715 7.70766C3.19938 8.4148 3.2 9.2931 3.2 10.4H4.8ZM5.82085 3.72317C4.91767 4.18336 4.18336 4.91767 3.72317 5.82085L5.14878 6.54723C5.45557 5.94511 5.94511 5.45557 6.54723 5.14878L5.82085 3.72317ZM13.6 4.8C14.7333 4.8 15.5352 4.80062 16.162 4.85184C16.7796 4.90229 17.1565 4.99783 17.4528 5.14878L18.1792 3.72317C17.6198 3.43814 17.0088 3.31569 16.2923 3.25715C15.5852 3.19938 14.7069 3.2 13.6 3.2V4.8ZM20.8 10.4C20.8 9.2931 20.8006 8.4148 20.7428 7.70766C20.6843 6.99118 20.5619 6.38024 20.2768 5.82085L18.8512 6.54723C19.0022 6.84348 19.0977 7.22042 19.1482 7.83795C19.1994 8.46483 19.2 9.26669 19.2 10.4H20.8ZM17.4528 5.14878C18.0549 5.45557 18.5444 5.94511 18.8512 6.54723L20.2768 5.82085C19.8166 4.91767 19.0823 4.18336 18.1792 3.72317L17.4528 5.14878ZM19.2 13.5V16H20.8V13.5H19.2ZM16 19.2H13.6V20.8H16V19.2ZM19.2 16C19.2 17.7673 17.7673 19.2 16 19.2V20.8C18.651 20.8 20.8 18.651 20.8 16H19.2ZM4.8 13.6V10.4H3.2V13.6H4.8ZM18.5657 16.0657L20.5657 14.0657L19.4343 12.9343L17.4343 14.9343L18.5657 16.0657ZM19.4343 14.0657L21.4343 16.0657L22.5657 14.9343L20.5657 12.9343L19.4343 14.0657ZM14.18 20.551L16.08 18.551L14.92 17.449L13.02 19.449L14.18 20.551ZM13.02 20.551L14.92 22.551L16.08 21.449L14.18 19.449L13.02 20.551Z"
      fill="white"
    />
  </svg>
)

Tools.displayName = 'Tools'
